import { useCallback } from 'react'
import {
    InfiniteData,
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query'
import { RequestResponse } from '@/shared/services/requestServices'
import { getPageSize } from '@/shared/utils/queryUtils'
import { QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import { CreateImportJobPayload, ImportJob, ImportJobStatus } from '../models/importModels'
import {
    createImportJobService,
    getImportJobByIdService,
    getImportJobsService,
    getWorkflowConfigsService
} from '../services/importServices'

export const importQueryKeys = {
    root: ['imports'] as const,
    infiniteJobs: (companyId: string, query?: QueryStringInput) => [...importQueryKeys.root, companyId, query],
    jobById: (jobId: string) => [...importQueryKeys.root, 'job', jobId],
    workflowConfigs: (companyId: string) => [...importQueryKeys.root, 'workflow-configs', companyId],
    errorSummary: (outputFilePath: string) => [...importQueryKeys.root, 'error-summary', outputFilePath]
}

const ACTIVE_STATUSES: ImportJobStatus[] = ['pending', 'processing']
const ACTIVE_JOB_POLL_INTERVAL_MS = 3000

type UseInfiniteImportJobs = {
    companyId: string
    query?: QueryStringInput
    isEnabled?: boolean
}

export const useInfiniteImportJobs = (
    options: UseInfiniteImportJobs
): UseInfiniteQueryResult<InfiniteData<RequestResponse<ImportJob[]> | undefined>> => {
    const { companyId, isEnabled, query } = options

    return useInfiniteQuery({
        queryKey: importQueryKeys.infiniteJobs(companyId, query),
        queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
            return getImportJobsService({
                companyId,
                query: {
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    },
                    ...query
                }
            })
        },
        enabled: isEnabled,
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },
        initialPageParam: undefined
    })
}

type UseImportJob = {
    jobId: string
    isEnabled?: boolean
}

export const useImportJob = ({
    jobId,
    isEnabled = true
}: UseImportJob): UseQueryResult<RequestResponse<ImportJob> | undefined> => {
    return useQuery({
        queryKey: importQueryKeys.jobById(jobId),
        queryFn: () => getImportJobByIdService(jobId),
        enabled: isEnabled && !!jobId,
        refetchInterval: (query) => {
            const status = query.state.data?.body?.status
            if (status && ACTIVE_STATUSES.includes(status)) return ACTIVE_JOB_POLL_INTERVAL_MS
            return false
        }
    })
}

export const useCreateImportJob = () => {
    return useMutation({
        mutationFn: (payload: CreateImportJobPayload) => createImportJobService(payload)
    })
}

type UseWorkflowConfigs = {
    companyId: string
}

export const useWorkflowConfigs = ({ companyId }: UseWorkflowConfigs) => {
    return useQuery({
        queryKey: importQueryKeys.workflowConfigs(companyId),
        queryFn: () => getWorkflowConfigsService(companyId),
        enabled: !!companyId
    })
}

type UseInvalidateImportJobs = {
    companyId: string
}

export const useInvalidateImportJobs = ({ companyId }: UseInvalidateImportJobs) => {
    const queryClient = useQueryClient()

    return useCallback(
        () => queryClient.invalidateQueries({ queryKey: [...importQueryKeys.root, companyId] }),
        [queryClient, companyId]
    )
}
